/**
 * 解析レール: 列マッピング・サブ設定・入力形式・詳細（多重比較など）
 * 解析タイプの選択はトップバーで行う。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion, Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import {
  normalizeAnalysisType,
  ttestNonparametricInferential,
  useKeisukeDashboardStore,
  type AnalysisOptions,
  type InputFormTypeKey,
} from '../../../../stores/systems/keisuke/dashboardStore';
import {
  coerceInputFormTypeToMatchAnalysis,
  getAllowedInputFormTypesForAnalysis,
} from '../../../../utils/keisukeInputFormUtils';
import { gridDataHasNumericValues, parseFiniteNumberFromCell } from '../../../../utils/keisukeGridData';
import { headersForValueColumnSelect } from '../../../../utils/keisukeStockColumnLabels';
import { recommendRegressionType } from '../../../../utils/keisukeRegressionRecommend';
import { tTestParametricInferenceLabel } from '../../../../utils/keisukeTTestDisplay';
import {
  buildDrTypeSwitchPatch,
  drMethodRequiresTotalColumn,
  drTypeRequiresTotalColumn,
  getAllowedDrMethods,
  normalizeDrMethodForType,
  normalizeDrType,
} from '../../../../utils/keisukeDoseResponseDefaults';
import { resolveOneWayPostHoc } from '../../../../utils/adapters/anovaAdapter';
import KeisukeAnalysisPicker from './keisukeAnalysisPicker';
import AnovaPostHocMethodInfoPanel from './AnovaPostHocMethodInfoPanel';
import DoseResponseMethodInfoPanel from './DoseResponseMethodInfoPanel';
import {
  KeisukeLabeledBinaryRadioGroup,
  KeisukeLabeledRadioGroup,
} from './keisukeDashboardBinaryRadioGroup';
import styles from './KeisukeDashboardLeftPane.module.scss';

const leftRailRadioProps = {
  labelClassName: styles.fieldLabel,
  groupClassName: styles.radioRow,
  pillClassName: styles.radioPill,
  pillActiveClassName: styles.radioPillActive,
} as const;

/** ラベル無し（details 内など）用 */
const leftRailPillProps = {
  groupClassName: styles.radioRow,
  pillClassName: styles.radioPill,
  pillActiveClassName: styles.radioPillActive,
} as const;

function ColSelect({
  id,
  value,
  onChange,
  label,
  headers,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  label: string;
  headers: string[];
}) {
  const options = value && !headers.includes(value) ? [value, ...headers] : headers;
  return (
    <div className={styles.fieldGroup}>
      <label className={styles.fieldLabel} htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        name={id}
        className="form-select form-select-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      >
        <option value="">—</option>
        {options.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function KeisukeDashboardLeftPane() {
  const { t } = useTranslation();
  const { gridHeaders, gridData, analysisOptions, setAnalysisOptions, inputFormType, setInputFormType } =
    useKeisukeDashboardStore(
      useShallow((s) => ({
        gridHeaders: s.gridHeaders,
        gridData: s.gridData,
        analysisOptions: s.analysisOptions,
        setAnalysisOptions: s.setAnalysisOptions,
        inputFormType: s.inputFormType,
        setInputFormType: s.setInputFormType,
      }))
    );

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const type = normalizeAnalysisType(analysisOptions.type);
  const anovaType = (analysisOptions.anovaType as string) ?? 'oneWay';
  const testType = (analysisOptions.testType as 'unpaired' | 'paired') ?? 'unpaired';
  const equalVariance = analysisOptions.equalVariance === true;
  const mapping = analysisOptions.mapping ?? {};
  const calibrationUnknownSamples = analysisOptions.calibrationUnknownSamples ?? [];
  const regressionMode = analysisOptions.regressionMode ?? 'scatter';
  const anovaRepeatedMethod = analysisOptions.anovaRepeatedMethod ?? 'repeated';
  const anovaOneWayMethod = analysisOptions.anovaOneWayMethod ?? 'anova';
  const twoWayMethod = analysisOptions.twoWayMethod ?? 'independent';
  const regressionType = analysisOptions.regressionType ?? 'standard';
  const regressionTypeManual = analysisOptions.regressionTypeManual === true;
  const postHocRaw = analysisOptions.postHoc as string | undefined;
  const postHoc = postHocRaw ?? '';
  const postHocMoreSelected =
    postHoc === 'holm' || postHoc === 'scheffe' || postHoc === 'dunnett';
  const [postHocMoreOpen, setPostHocMoreOpen] = useState(postHocMoreSelected);

  useEffect(() => {
    setPostHocMoreOpen(postHocMoreSelected);
  }, [postHocMoreSelected]);

  const twoWayMethodMoreSelected =
    twoWayMethod === 'lmm' || twoWayMethod === 'full-repeated';
  const [twoWayMethodMoreOpen, setTwoWayMethodMoreOpen] = useState(twoWayMethodMoreSelected);

  useEffect(() => {
    setTwoWayMethodMoreOpen(twoWayMethodMoreSelected);
  }, [twoWayMethodMoreSelected]);

  const regressionChartPoints = useMemo(() => {
    const xCol = mapping.doseColumn;
    const yCol = mapping.valueColumn;
    if (!xCol || !yCol) return [];
    const points: Array<{ x: number; y: number }> = [];
    for (const row of gridData) {
      const x = parseFiniteNumberFromCell(row[xCol]);
      const y = parseFiniteNumberFromCell(row[yCol]);
      if (x !== null && y !== null) points.push({ x, y });
    }
    return points;
  }, [gridData, mapping.doseColumn, mapping.valueColumn]);

  const recommendedRegressionType = useMemo(
    () => recommendRegressionType(regressionChartPoints),
    [regressionChartPoints]
  );

  useEffect(() => {
    if (type !== 'regression' || regressionTypeManual) return;
    if (!recommendedRegressionType || regressionType === recommendedRegressionType) return;
    setAnalysisOptions({ regressionType: recommendedRegressionType });
  }, [
    type,
    regressionTypeManual,
    recommendedRegressionType,
    regressionType,
    setAnalysisOptions,
  ]);

  const anovaGroupNames = useMemo(() => {
    if (type !== 'anova') return [] as string[];
    const groupCol = mapping.groupColumn ?? mapping.factorAColumn;
    if (!groupCol) return [];
    const names = new Set<string>();
    for (const row of gridData) {
      const name = String(row[groupCol] ?? '').trim();
      if (name) names.add(name);
    }
    return Array.from(names);
  }, [type, gridData, mapping.groupColumn, mapping.factorAColumn]);

  /** エンジンと同じ解決結果。未設定＋3群以上の自動 Tukey を「なし」と誤表示しない */
  const resolvedPostHoc = useMemo(
    () => resolveOneWayPostHoc(anovaGroupNames.length, postHocRaw),
    [anovaGroupNames.length, postHocRaw]
  );

  const twoWayFullRepeatedConfirm = analysisOptions.twoWayFullRepeatedConfirm === true;
  const ttestInferential = analysisOptions.ttestInferential ?? 'parametric';

  const allowedInputForms = useMemo(
    () =>
      getAllowedInputFormTypesForAnalysis({
        type: analysisOptions.type,
        anovaType: analysisOptions.anovaType,
      }),
    [analysisOptions.type, analysisOptions.anovaType]
  );

  const effectiveInputForm = useMemo(
    () =>
      coerceInputFormTypeToMatchAnalysis(inputFormType, {
        type: analysisOptions.type,
        anovaType: analysisOptions.anovaType,
      }),
    [inputFormType, analysisOptions.type, analysisOptions.anovaType]
  );

  const inputFormOptions: { value: InputFormTypeKey; labelKey: string }[] = [
    { value: 'tTestTwoColumns', labelKey: 'keisuke.dashboard.inputForm.tTestTwoColumns' },
    { value: 'oneWayColumns', labelKey: 'keisuke.dashboard.inputForm.oneWayColumns' },
    { value: 'twoWayBlocks', labelKey: 'keisuke.dashboard.inputForm.twoWayBlocks' },
    { value: 'doseResponseForm', labelKey: 'keisuke.dashboard.inputForm.doseResponseForm' },
    { value: 'survivalForm', labelKey: 'keisuke.dashboard.inputForm.survivalForm' },
    { value: 'repeatedForm', labelKey: 'keisuke.dashboard.inputForm.repeatedForm' },
    { value: 'longFormat', labelKey: 'keisuke.dashboard.inputForm.longFormat' },
  ];

  const updateOptions = useCallback(
    (patch: Partial<AnalysisOptions>) => {
      setAnalysisOptions(patch);
    },
    [setAnalysisOptions]
  );

  /** 空グリッドで ANOVA デザイン切替時、前解析の列 mapping を捨ててコンパクト表を汚さない */
  const setAnovaType = useCallback(
    (next: 'oneWay' | 'twoWay' | 'repeatedMeasures') => {
      if (gridDataHasNumericValues(gridHeaders, gridData)) {
        updateOptions({ anovaType: next });
        return;
      }
      updateOptions({
        anovaType: next,
        mapping: {
          ...mapping,
          groupColumn: undefined,
          factorAColumn: undefined,
          factorBColumn: undefined,
          valueColumn: undefined,
          subjectColumn: undefined,
        },
      });
    },
    [gridData, gridHeaders, mapping, updateOptions]
  );

  const updateMapping = useCallback(
    (key: keyof NonNullable<AnalysisOptions['mapping']>, value: string) => {
      setAnalysisOptions({ mapping: { ...mapping, [key]: value || undefined } });
    },
    [setAnalysisOptions, mapping]
  );

  /** 値列セレクト: 群っぽい列・非数値列を除外（基本統計と同じ） */
  const valueColumnSelectHeaders = useMemo(
    () => headersForValueColumnSelect(gridHeaders, gridData, mapping.valueColumn),
    [gridHeaders, gridData, mapping.valueColumn]
  );

  /** 長形式 t検定: 対応あり／なしで列マッピング UI だけ差し替え */
  const tTestLongFormatMappingFields = useMemo(() => {
    if (testType === 'paired') {
      return [
        {
          id: 'lp-ttest-before',
          key: 'beforeColumn' as const,
          label: t('keisuke.dashboard.ttest.beforeColumn'),
        },
        {
          id: 'lp-ttest-after',
          key: 'afterColumn' as const,
          label: t('keisuke.dashboard.ttest.afterColumn'),
        },
      ];
    }
    return [
      {
        id: 'lp-ttest-group',
        key: 'groupColumn' as const,
        label: t('keisuke.dashboard.ttest.groupColumn'),
      },
      {
        id: 'lp-ttest-value',
        key: 'valueColumn' as const,
        label: t('keisuke.dashboard.ttest.valueColumn'),
      },
    ];
  }, [t, testType]);

  return (
    <div className={styles.wrap}>
      <KeisukeAnalysisPicker />

      <div className={styles.body}>
        {/* ── t検定（長形式のみ左に出す。2列表は中央へ同居済み） ── */}
        {type === 't-test' && effectiveInputForm !== 'tTestTwoColumns' && (
          <div className={styles.settingsSection}>
            <KeisukeLabeledBinaryRadioGroup
              label={t('keisuke.dashboard.ttest.testType')}
              className={styles.fieldGroup}
              {...leftRailRadioProps}
              first={{
                active: testType === 'unpaired',
                onSelect: () => updateOptions({ testType: 'unpaired' }),
                children: t('keisuke.dashboard.ttest.unpaired'),
              }}
              second={{
                active: testType === 'paired',
                onSelect: () => updateOptions({ testType: 'paired' }),
                children: t('keisuke.dashboard.ttest.paired'),
              }}
            />

            <KeisukeLabeledBinaryRadioGroup
              label={t('keisuke.dashboard.ttest.inferential')}
              className={styles.fieldGroup}
              {...leftRailRadioProps}
              first={{
                active: ttestInferential === 'parametric',
                onSelect: () => updateOptions({ ttestInferential: 'parametric' }),
                children: tTestParametricInferenceLabel(t),
              }}
              second={{
                active: ttestInferential !== 'parametric',
                onSelect: () =>
                  updateOptions({
                    ttestInferential: ttestNonparametricInferential(testType),
                  }),
                children:
                  testType === 'paired'
                    ? t('keisuke.dashboard.ttest.wilcoxon')
                    : t('keisuke.dashboard.ttest.mannWhitney'),
              }}
            />

            {ttestInferential === 'parametric' && testType === 'unpaired' && (
              <div className={styles.fieldGroup}>
                <KeisukeLabeledBinaryRadioGroup
                  label={t('keisuke.dashboard.ttest.varianceMethod')}
                  {...leftRailRadioProps}
                  first={{
                    active: !equalVariance,
                    onSelect: () => updateOptions({ equalVariance: false }),
                    children: (
                      <>
                        {t('keisuke.dashboard.ttest.welch')}
                        <span className={styles.recommendedBadge} aria-hidden>
                          {t('keisuke.dashboard.ttest.recommendedShort')}
                        </span>
                      </>
                    ),
                  }}
                  second={{
                    active: equalVariance,
                    onSelect: () => updateOptions({ equalVariance: true }),
                    children: t('keisuke.dashboard.ttest.student'),
                  }}
                />
                {equalVariance ? (
                  <p className={styles.fieldHint}>
                    {t('keisuke.dashboard.ttest.studentHint')}
                  </p>
                ) : null}
              </div>
            )}

            {tTestLongFormatMappingFields.map((field) => (
              <ColSelect
                key={field.id}
                id={field.id}
                value={mapping[field.key] ?? ''}
                onChange={(v) => updateMapping(field.key, v)}
                label={field.label}
                headers={
                  field.key === 'valueColumn' ? valueColumnSelectHeaders : gridHeaders
                }
              />
            ))}
          </div>
        )}

        {/* ── ANOVA 設定 ── */}
        {type === 'anova' && (
          <div className={styles.settingsSection}>
            <KeisukeLabeledRadioGroup
              className={styles.fieldGroup}
              label={t('keisuke.dashboard.anova.type')}
              {...leftRailRadioProps}
              options={[
                {
                  key: 'oneWay',
                  active: anovaType !== 'twoWay' && anovaType !== 'repeatedMeasures',
                  onSelect: () => setAnovaType('oneWay'),
                  children: t('keisuke.dashboard.anova.oneWay'),
                },
                {
                  key: 'twoWay',
                  active: anovaType === 'twoWay',
                  onSelect: () => setAnovaType('twoWay'),
                  children: t('keisuke.dashboard.anova.twoWay'),
                },
                {
                  key: 'repeatedMeasures',
                  active: anovaType === 'repeatedMeasures',
                  onSelect: () => setAnovaType('repeatedMeasures'),
                  children: t('keisuke.dashboard.anova.repeatedMeasures'),
                },
              ]}
            />

            <KeisukeLabeledRadioGroup
              className={styles.fieldGroup}
              label={t('keisuke.dashboard.accordion.postHoc')}
              {...leftRailRadioProps}
              options={(
                [
                  { value: '', label: t('keisuke.dashboard.postHoc.none') },
                  { value: 'tukey', label: 'Tukey' },
                  { value: 'bonferroni', label: 'Bonferroni' },
                ] as const
              ).map((opt) => ({
                key: opt.value || 'none',
                active:
                  opt.value === ''
                    ? resolvedPostHoc == null
                    : resolvedPostHoc === opt.value,
                onSelect: () => updateOptions({ postHoc: opt.value }),
                children: opt.label,
              }))}
            />
            <div className={styles.fieldGroup}>
              <details
                className={styles.postHocMore}
                open={postHocMoreOpen}
                onToggle={(e) => {
                  const next = (e.currentTarget as HTMLDetailsElement).open;
                  if (postHocMoreSelected && !next) {
                    setPostHocMoreOpen(true);
                    return;
                  }
                  setPostHocMoreOpen(next);
                }}
              >
                <summary className={styles.postHocMoreSummary}>
                  {t('keisuke.dashboard.postHoc.moreMethods')}
                </summary>
                <KeisukeLabeledRadioGroup
                  label={t('keisuke.dashboard.postHoc.moreMethods')}
                  hideLabel
                  {...leftRailPillProps}
                  options={(
                    [
                      { value: 'holm', label: t('keisuke.anova.postHoc.holm') },
                      { value: 'scheffe', label: t('keisuke.anova.postHoc.scheffe') },
                      { value: 'dunnett', label: t('keisuke.anova.postHoc.dunnett') },
                    ] as const
                  ).map((opt) => ({
                    key: opt.value,
                    active: resolvedPostHoc === opt.value,
                    onSelect: () => updateOptions({ postHoc: opt.value }),
                    children: opt.label,
                  }))}
                />
              </details>
            </div>

            {resolvedPostHoc === 'dunnett' && anovaGroupNames.length > 0 && (
              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>
                  {t('keisuke.qpcr.deltaDeltaCtMethod.controlGroup')}
                </div>
                <select
                  className="form-select form-select-sm"
                  value={
                    analysisOptions.anovaControlGroup &&
                    anovaGroupNames.includes(analysisOptions.anovaControlGroup)
                      ? analysisOptions.anovaControlGroup
                      : anovaGroupNames.find((n) => n.toLowerCase() === 'control') ??
                        anovaGroupNames[0]
                  }
                  onChange={(e) => updateOptions({ anovaControlGroup: e.target.value })}
                  aria-label={t('keisuke.qpcr.deltaDeltaCtMethod.controlGroup')}
                >
                  {anovaGroupNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <AnovaPostHocMethodInfoPanel postHoc={resolvedPostHoc ?? ''} />

            {(anovaType === 'oneWay' || (anovaType !== 'twoWay' && anovaType !== 'repeatedMeasures')) && (
              <>
                <KeisukeLabeledBinaryRadioGroup
                  label={t('keisuke.dashboard.anova.oneWayMethod')}
                  className={styles.fieldGroup}
                  {...leftRailRadioProps}
                  first={{
                    active: anovaOneWayMethod === 'anova',
                    onSelect: () => updateOptions({ anovaOneWayMethod: 'anova' }),
                    children: t('keisuke.dashboard.anova.oneWayAnova'),
                  }}
                  second={{
                    active: anovaOneWayMethod === 'kruskal-wallis',
                    onSelect: () => updateOptions({ anovaOneWayMethod: 'kruskal-wallis' }),
                    children: t('keisuke.dashboard.anova.kruskalWallis'),
                  }}
                />
                {effectiveInputForm !== 'oneWayColumns' ? (
                  <>
                    <ColSelect
                      id="lp-anova-1w-group"
                      value={mapping.groupColumn ?? mapping.factorAColumn ?? ''}
                      onChange={(v) => {
                        updateMapping('groupColumn', v);
                        updateMapping('factorAColumn', v);
                      }}
                      label={t('keisuke.dashboard.anova.groupColumn')}
                      headers={gridHeaders}
                    />
                    <ColSelect
                      id="lp-anova-1w-value"
                      value={mapping.valueColumn ?? ''}
                      onChange={(v) => updateMapping('valueColumn', v)}
                      label={t('keisuke.dashboard.ttest.valueColumn')}
                      headers={valueColumnSelectHeaders}
                    />
                  </>
                ) : (
                  <p className={styles.fieldHint}>
                    {t('keisuke.dashboard.anova.mappingFromGridHint')}
                  </p>
                )}
              </>
            )}

            {anovaType === 'twoWay' && (
              <>
                <div className={styles.fieldGroup}>
                  <KeisukeLabeledBinaryRadioGroup
                    label={t('keisuke.dashboard.anova.twoWayMethod')}
                    {...leftRailRadioProps}
                    first={{
                      active: twoWayMethod === 'independent',
                      onSelect: () => updateOptions({ twoWayMethod: 'independent' }),
                      children: t('keisuke.dashboard.anova.twoWayIndependent'),
                    }}
                    second={{
                      active: twoWayMethod === 'mixed',
                      onSelect: () => updateOptions({ twoWayMethod: 'mixed' }),
                      children: t('keisuke.dashboard.anova.twoWayMixed'),
                    }}
                  />
                  <details
                    className={styles.postHocMore}
                    open={twoWayMethodMoreOpen}
                    onToggle={(e) => {
                      const next = (e.currentTarget as HTMLDetailsElement).open;
                      if (twoWayMethodMoreSelected && !next) {
                        setTwoWayMethodMoreOpen(true);
                        return;
                      }
                      setTwoWayMethodMoreOpen(next);
                    }}
                  >
                    <summary className={styles.postHocMoreSummary}>
                      {t('keisuke.dashboard.anova.twoWayMoreMethods')}
                    </summary>
                    <KeisukeLabeledRadioGroup
                      label={t('keisuke.dashboard.anova.twoWayMoreMethods')}
                      hideLabel
                      {...leftRailPillProps}
                      options={(
                        [
                          {
                            value: 'lmm' as const,
                            label: t('keisuke.anova.methodLabels.oneWay.lmm'),
                          },
                          {
                            value: 'full-repeated' as const,
                            label: t('keisuke.anova.methodLabels.twoWay.fullRepeated'),
                          },
                        ] as const
                      ).map((opt) => ({
                        key: opt.value,
                        active: twoWayMethod === opt.value,
                        onSelect: () => updateOptions({ twoWayMethod: opt.value }),
                        children: opt.label,
                      }))}
                    />
                  </details>
                </div>
                {(twoWayMethod === 'mixed' ||
                  twoWayMethod === 'lmm' ||
                  twoWayMethod === 'full-repeated') && (
                  <ColSelect
                    id="lp-anova-2w-subject"
                    value={mapping.subjectColumn ?? ''}
                    onChange={(v) => updateMapping('subjectColumn', v)}
                    label={t('keisuke.dashboard.anova.subjectColumn')}
                    headers={gridHeaders}
                  />
                )}
                {twoWayMethod === 'full-repeated' && (
                  <div className={styles.fieldGroup}>
                    <p className="small text-muted mb-2">
                      {t('keisuke.anova.twoWay.fullRepeatedNote')}
                    </p>
                    <label className={styles.fieldLabelCheck} htmlFor="lp-anova-2w-full-confirm">
                      <input
                        id="lp-anova-2w-full-confirm"
                        type="checkbox"
                        checked={twoWayFullRepeatedConfirm}
                        onChange={(e) =>
                          updateOptions({ twoWayFullRepeatedConfirm: e.target.checked })
                        }
                      />
                      {t('keisuke.anova.errors.fullRepeatedConfirmation')}
                    </label>
                  </div>
                )}
                {effectiveInputForm !== 'twoWayBlocks' ? (
                  <>
                    <ColSelect
                      id="lp-anova-2w-fa"
                      value={mapping.factorAColumn ?? mapping.groupColumn ?? ''}
                      onChange={(v) => {
                        updateMapping('factorAColumn', v);
                        updateMapping('groupColumn', v);
                      }}
                      label={t('keisuke.dashboard.anova.factorAColumn')}
                      headers={gridHeaders}
                    />
                    <ColSelect
                      id="lp-anova-2w-fb"
                      value={mapping.factorBColumn ?? ''}
                      onChange={(v) => updateMapping('factorBColumn', v)}
                      label={t('keisuke.dashboard.anova.factorBColumn')}
                      headers={gridHeaders}
                    />
                    <ColSelect
                      id="lp-anova-2w-value"
                      value={mapping.valueColumn ?? ''}
                      onChange={(v) => updateMapping('valueColumn', v)}
                      label={t('keisuke.dashboard.ttest.valueColumn')}
                      headers={valueColumnSelectHeaders}
                    />
                  </>
                ) : (
                  <p className={styles.fieldHint}>
                    {t('keisuke.dashboard.anova.mappingFromGridHint')}
                  </p>
                )}
              </>
            )}

            {anovaType === 'repeatedMeasures' && (
              <>
                <KeisukeLabeledBinaryRadioGroup
                  label={t('keisuke.dashboard.anova.repeatedMethod')}
                  className={styles.fieldGroup}
                  {...leftRailRadioProps}
                  first={{
                    active: anovaRepeatedMethod === 'repeated',
                    onSelect: () => updateOptions({ anovaRepeatedMethod: 'repeated' }),
                    children: t('keisuke.dashboard.anova.repeatedClassical'),
                  }}
                  second={{
                    active: anovaRepeatedMethod === 'lmm',
                    onSelect: () => updateOptions({ anovaRepeatedMethod: 'lmm' }),
                    children: t('keisuke.anova.methodLabels.oneWay.lmm'),
                  }}
                />
                {effectiveInputForm !== 'repeatedForm' ? (
                  <>
                    <ColSelect
                      id="lp-anova-rm-factor"
                      value={mapping.groupColumn ?? mapping.factorAColumn ?? ''}
                      onChange={(v) => {
                        updateMapping('groupColumn', v);
                        updateMapping('factorAColumn', v);
                      }}
                      label={t('keisuke.dashboard.anova.withinFactorColumn')}
                      headers={gridHeaders}
                    />
                    <ColSelect
                      id="lp-anova-rm-value"
                      value={mapping.valueColumn ?? ''}
                      onChange={(v) => updateMapping('valueColumn', v)}
                      label={t('keisuke.dashboard.ttest.valueColumn')}
                      headers={valueColumnSelectHeaders}
                    />
                    <ColSelect
                      id="lp-anova-rm-subject"
                      value={mapping.subjectColumn ?? ''}
                      onChange={(v) => updateMapping('subjectColumn', v)}
                      label={t('keisuke.dashboard.anova.subjectColumn')}
                      headers={gridHeaders}
                    />
                  </>
                ) : (
                  <p className={styles.fieldHint}>
                    {t('keisuke.dashboard.anova.mappingFromGridHint')}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* ── 用量反応 設定 ── */}
        {type === 'dose-response' && (
          <div className={styles.settingsSection}>
            <KeisukeLabeledRadioGroup
              className={styles.fieldGroup}
              label={t('keisuke.dashboard.doseResponse.type')}
              {...leftRailRadioProps}
              options={(['ld50', 'ec50', 'ec90', 'tcid50'] as const).map((v) => ({
                key: v,
                active: normalizeDrType(analysisOptions.drType) === v,
                onSelect: () => updateOptions(buildDrTypeSwitchPatch(v, analysisOptions)),
                children:
                  v === 'ld50' ? 'LD50' : v === 'ec50' ? 'EC50' : v === 'ec90' ? 'EC90' : 'TCID50',
              }))}
            />
            <div className={styles.fieldGroup}>
              {normalizeDrType(analysisOptions.drType) === 'ec50' && (
                <p className="small text-muted mb-2">{t('keisuke.dashboard.doseResponse.ec50Note')}</p>
              )}
              {normalizeDrType(analysisOptions.drType) === 'ec90' && (
                <p className="small text-muted mb-2">{t('keisuke.dashboard.doseResponse.ec90Note')}</p>
              )}
              {normalizeDrType(analysisOptions.drType) === 'tcid50' && (
                <p className="small text-muted mb-2">{t('keisuke.dashboard.doseResponse.tcid50Note')}</p>
              )}
            </div>

            <KeisukeLabeledRadioGroup
              className={styles.fieldGroup}
              label={t('keisuke.dashboard.doseResponse.method')}
              {...leftRailRadioProps}
              options={(
                [
                  ['probit', 'Probit'],
                  ['logistic', 'Logistic'],
                  ['reed-muench', 'Reed-Muench'],
                  ['spearman-karber', 'Spearman-Kärber'],
                ] as const
              )
                .filter(([value]) =>
                  getAllowedDrMethods(normalizeDrType(analysisOptions.drType)).includes(value)
                )
                .map(([value, label]) => ({
                  key: value,
                  active:
                    normalizeDrMethodForType(
                      normalizeDrType(analysisOptions.drType),
                      analysisOptions.drMethod
                    ) === value,
                  onSelect: () => updateOptions({ drMethod: value }),
                  children: label,
                }))}
            />

            <DoseResponseMethodInfoPanel
              method={normalizeDrMethodForType(
                normalizeDrType(analysisOptions.drType),
                analysisOptions.drMethod
              )}
            />

            {effectiveInputForm !== 'doseResponseForm' ? (
              <>
                <ColSelect
                  id="lp-dr-dose"
                  value={mapping.doseColumn ?? ''}
                  onChange={(v) => updateMapping('doseColumn', v)}
                  label={t('keisuke.dashboard.doseResponse.doseColumn')}
                  headers={gridHeaders}
                />
                <ColSelect
                  id="lp-dr-effect"
                  value={mapping.effectColumn ?? ''}
                  onChange={(v) => updateMapping('effectColumn', v)}
                  label={t('keisuke.dashboard.doseResponse.effectColumn')}
                  headers={gridHeaders}
                />
                <ColSelect
                  id="lp-dr-total"
                  value={mapping.totalColumn ?? ''}
                  onChange={(v) => updateMapping('totalColumn', v)}
                  label={t('keisuke.dashboard.doseResponse.totalColumn')}
                  headers={gridHeaders}
                />
              </>
            ) : (
              <p className={styles.fieldHint}>
                {t('keisuke.dashboard.anova.mappingFromGridHint')}
              </p>
            )}
            {(drTypeRequiresTotalColumn(normalizeDrType(analysisOptions.drType)) ||
              drMethodRequiresTotalColumn(
                normalizeDrMethodForType(
                  normalizeDrType(analysisOptions.drType),
                  analysisOptions.drMethod
                )
              )) && (
              <p className="small text-muted mb-0">{t('keisuke.dashboard.doseResponse.totalColumnRequired')}</p>
            )}
          </div>
        )}

        {type === 'basic-stats' && (
          <div className={styles.settingsSection}>
            <ColSelect
              id="lp-basic-value"
              value={mapping.valueColumn ?? ''}
              onChange={(v) => updateMapping('valueColumn', v)}
              label={t('keisuke.dashboard.ttest.valueColumn')}
              headers={valueColumnSelectHeaders}
            />
          </div>
        )}

        {type === 'qpcr' && (
          <div className={styles.settingsSection}>
            <KeisukeLabeledBinaryRadioGroup
              label={t('keisuke.qpcr.method')}
              className={styles.fieldGroup}
              {...leftRailRadioProps}
              first={{
                active: (analysisOptions.qpcrMethod ?? 'deltaCt') === 'deltaCt',
                onSelect: () => updateOptions({ qpcrMethod: 'deltaCt' }),
                children: 'ΔCt',
              }}
              second={{
                active: analysisOptions.qpcrMethod === 'deltaDeltaCt',
                onSelect: () => updateOptions({ qpcrMethod: 'deltaDeltaCt' }),
                children: 'ΔΔCt',
              }}
            />
            <ColSelect
              id="lp-qpcr-sample"
              value={mapping.groupColumn ?? ''}
              onChange={(v) => updateMapping('groupColumn', v)}
              label={t('keisuke.qpcr.sampleNameColumn')}
              headers={gridHeaders}
            />
            <ColSelect
              id="lp-qpcr-hk"
              value={mapping.beforeColumn ?? ''}
              onChange={(v) => updateMapping('beforeColumn', v)}
              label={t('keisuke.qpcr.housekeepingColumn')}
              headers={gridHeaders}
            />
            <ColSelect
              id="lp-qpcr-target"
              value={mapping.valueColumn ?? ''}
              onChange={(v) => updateMapping('valueColumn', v)}
              label={t('keisuke.qpcr.targetColumn')}
              headers={gridHeaders}
            />
            <p className={styles.railHint}>
              {t('keisuke.qpcr.deltaDeltaCtNameHint')}
            </p>
          </div>
        )}

        {type === 'longitudinal' && (
          <div className={styles.settingsSection}>
            <ColSelect
              id="lp-long-subject"
              value={mapping.subjectColumn ?? ''}
              onChange={(v) => updateMapping('subjectColumn', v)}
              label={t('keisuke.dashboard.anova.subjectColumn')}
              headers={gridHeaders}
            />
            <ColSelect
              id="lp-long-group"
              value={mapping.groupColumn ?? ''}
              onChange={(v) => updateMapping('groupColumn', v)}
              label={t('keisuke.dashboard.anova.groupColumn')}
              headers={gridHeaders}
            />
            <ColSelect
              id="lp-long-time"
              value={mapping.timeColumn ?? ''}
              onChange={(v) => updateMapping('timeColumn', v)}
              label={t('keisuke.dashboard.survival.timeColumn')}
              headers={gridHeaders}
            />
            <ColSelect
              id="lp-long-value"
              value={mapping.valueColumn ?? ''}
              onChange={(v) => updateMapping('valueColumn', v)}
              label={t('keisuke.dashboard.ttest.valueColumn')}
              headers={valueColumnSelectHeaders}
            />
          </div>
        )}

        {type === 'regression' && (
          <div className={styles.settingsSection}>
            <KeisukeLabeledBinaryRadioGroup
              label={t('keisuke.workspace.regressionMode')}
              className={styles.fieldGroup}
              {...leftRailRadioProps}
              first={{
                active: (analysisOptions.regressionMode ?? 'scatter') === 'scatter',
                onSelect: () => updateOptions({ regressionMode: 'scatter' }),
                children: t('keisuke.tabs.scatterPlot'),
              }}
              second={{
                active: analysisOptions.regressionMode === 'calibration',
                onSelect: () => {
                  const patch: Partial<AnalysisOptions> = { regressionMode: 'calibration' };
                  if (!analysisOptions.calibrationUnknownSamples?.length) {
                    patch.calibrationUnknownSamples = [
                      {
                        name: t('keisuke.calibrationCurve.sampleData.unknownSampleName', {
                          index: 1,
                        }),
                        absorbance: 0,
                      },
                    ];
                  }
                  updateOptions(patch);
                },
                children: t('keisuke.tabs.calibrationCurve'),
              }}
            />
            <div className={styles.fieldGroup}>
              <KeisukeLabeledBinaryRadioGroup
                label={t('keisuke.scatterPlot.regressionType')}
                {...leftRailRadioProps}
                first={{
                  active: regressionType === 'standard',
                  onSelect: () =>
                    updateOptions({ regressionType: 'standard', regressionTypeManual: true }),
                  children: t('keisuke.scatterPlot.regressionStandard'),
                }}
                second={{
                  active: regressionType === 'origin',
                  onSelect: () =>
                    updateOptions({ regressionType: 'origin', regressionTypeManual: true }),
                  children: t('keisuke.scatterPlot.regressionOrigin'),
                }}
              />
              {recommendedRegressionType && !regressionTypeManual && (
                <p className={styles.railHint}>
                  {recommendedRegressionType === 'origin'
                    ? t('keisuke.scatterPlot.autoRegressionOrigin')
                    : t('keisuke.scatterPlot.autoRegressionStandard')}
                </p>
              )}
            </div>
            {regressionMode === 'calibration' && (
              <div className={styles.settingsSection}>
                <div className={styles.fieldLabel}>
                  {t('keisuke.calibrationCurve.unknownSamples')}
                </div>
                <p className={styles.railHint}>
                  {t('keisuke.workspace.calibrationUnknownHint')}
                </p>
                {calibrationUnknownSamples.length === 0 ? (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={() =>
                      updateOptions({
                        calibrationUnknownSamples: [
                          {
                            name: t('keisuke.calibrationCurve.sampleData.unknownSampleName', {
                              index: 1,
                            }),
                            absorbance: 0,
                          },
                        ],
                      })
                    }
                  >
                    {t('keisuke.calibrationCurve.addFirstSample')}
                  </Button>
                ) : (
                  <>
                    <Table bordered size="sm" className="mb-2">
                      <thead>
                        <tr>
                          <th>{t('keisuke.calibrationCurve.sampleName')}</th>
                          <th>{t('keisuke.calibrationCurve.absorbance')}</th>
                          <th aria-hidden />
                        </tr>
                      </thead>
                      <tbody>
                        {calibrationUnknownSamples.map((sample, index) => (
                          <tr key={`unknown-${index}`}>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={sample.name}
                                onChange={(e) => {
                                  const next = [...calibrationUnknownSamples];
                                  next[index] = { ...sample, name: e.target.value };
                                  updateOptions({ calibrationUnknownSamples: next });
                                }}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                step="any"
                                className="form-control form-control-sm"
                                value={Number.isFinite(sample.absorbance) ? sample.absorbance : ''}
                                onChange={(e) => {
                                  const next = [...calibrationUnknownSamples];
                                  next[index] = {
                                    ...sample,
                                    absorbance: parseFloat(e.target.value) || 0,
                                  };
                                  updateOptions({ calibrationUnknownSamples: next });
                                }}
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                aria-label={t('keisuke.common.delete')}
                                onClick={() =>
                                  updateOptions({
                                    calibrationUnknownSamples: calibrationUnknownSamples.filter(
                                      (_, i) => i !== index
                                    ),
                                  })
                                }
                              >
                                <i className="fas fa-trash" aria-hidden />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        updateOptions({
                          calibrationUnknownSamples: [
                            ...calibrationUnknownSamples,
                            {
                              name: t('keisuke.calibrationCurve.sampleData.unknownSampleName', {
                                index: calibrationUnknownSamples.length + 1,
                              }),
                              absorbance: 0,
                            },
                          ],
                        })
                      }
                    >
                      {t('keisuke.calibrationCurve.addSample')}
                    </Button>
                  </>
                )}
              </div>
            )}
            <ColSelect
              id="lp-reg-x"
              value={mapping.doseColumn ?? ''}
              onChange={(v) => updateMapping('doseColumn', v)}
              label={t('keisuke.sampleData.columns.x')}
              headers={gridHeaders}
            />
            <ColSelect
              id="lp-reg-y"
              value={mapping.valueColumn ?? ''}
              onChange={(v) => updateMapping('valueColumn', v)}
              label={t('keisuke.sampleData.columns.y')}
              headers={gridHeaders}
            />
          </div>
        )}

        {type === 'bar-chart' && (
          <div className={styles.settingsSection}>
            <p className={styles.railHint}>
              {t('keisuke.workspace.barChartHint')}
            </p>
            <ColSelect
              id="lp-bar-group"
              value={mapping.groupColumn ?? ''}
              onChange={(v) => updateMapping('groupColumn', v)}
              label={t('keisuke.dashboard.ttest.groupColumn')}
              headers={gridHeaders}
            />
            <ColSelect
              id="lp-bar-value"
              value={mapping.valueColumn ?? ''}
              onChange={(v) => updateMapping('valueColumn', v)}
              label={t('keisuke.dashboard.ttest.valueColumn')}
              headers={valueColumnSelectHeaders}
            />
          </div>
        )}

        {/* ── 生存曲線 設定 ── */}
        {type === 'survival' && (
          <div className={styles.settingsSection}>
            {effectiveInputForm !== 'survivalForm' ? (
              <>
                <ColSelect
                  id="lp-survival-group"
                  value={mapping.survivalGroupColumn ?? mapping.groupColumn ?? ''}
                  onChange={(v) => {
                    updateMapping('survivalGroupColumn', v);
                    updateMapping('groupColumn', v);
                  }}
                  label={t('keisuke.dashboard.survival.groupColumn')}
                  headers={gridHeaders}
                />
                <ColSelect
                  id="lp-survival-time"
                  value={mapping.timeColumn ?? ''}
                  onChange={(v) => updateMapping('timeColumn', v)}
                  label={t('keisuke.dashboard.survival.timeColumn')}
                  headers={gridHeaders}
                />
                <ColSelect
                  id="lp-survival-event"
                  value={mapping.eventColumn ?? ''}
                  onChange={(v) => updateMapping('eventColumn', v)}
                  label={t('keisuke.dashboard.survival.eventColumn')}
                  headers={gridHeaders}
                />
              </>
            ) : (
              <p className={styles.fieldHint}>
                {t('keisuke.dashboard.anova.mappingFromGridHint')}
              </p>
            )}
          </div>
        )}

        {/* 入力形式はモード切替なので詳細アコーディオンの外（常時表示） */}
        <div className={`${styles.fieldGroup} ${styles.inputFormField}`}>
          <label className={styles.fieldLabel} htmlFor="lp-input-form">
            {t('keisuke.dashboard.inputFormLabel')}
          </label>
          <select
            id="lp-input-form"
            className="form-select form-select-sm"
            value={effectiveInputForm}
            onChange={(e) => setInputFormType(e.target.value as InputFormTypeKey)}
          >
            {inputFormOptions
              .filter((o) => allowedInputForms.includes(o.value))
              .map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
          </select>
        </div>

        {/* 詳細に出す項目が無いとき（例: ノンパラ t検定）はアコーディオン自体を出さない */}
        {type === 't-test' && ttestInferential === 'parametric' ? (
          <Accordion
            className={styles.advancedAccordion}
            activeKey={advancedOpen ? 'advanced' : null}
            onSelect={(key) => setAdvancedOpen(key === 'advanced')}
          >
            <Accordion.Item eventKey="advanced">
              <Accordion.Header>
                {t('keisuke.workspace.advancedSettings')}
              </Accordion.Header>
              <Accordion.Body>
                {/*
                  RB Accordion.Body は閉じても DOM に残ることがある。
                  開いているときだけマウントし、折りたたみ時の読み上げ漏れを防ぐ。
                */}
                {advancedOpen ? (
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="lp-ttest-comparison-count">
                      {t('keisuke.ttest.comparisonCount')}
                    </label>
                    <input
                      id="lp-ttest-comparison-count"
                      type="number"
                      min={1}
                      step={1}
                      className="form-control form-control-sm"
                      value={analysisOptions.ttestComparisonCount ?? 1}
                      onChange={(e) =>
                        updateOptions({
                          ttestComparisonCount: Math.max(1, Number(e.target.value) || 1),
                        })
                      }
                      aria-label={t('keisuke.ttest.comparisonCount')}
                      aria-describedby="lp-ttest-comparison-count-hint"
                    />
                    <p id="lp-ttest-comparison-count-hint" className={styles.fieldHint}>
                      {t('keisuke.ttest.comparisonCountHint')}
                    </p>
                  </div>
                ) : null}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        ) : null}
      </div>
    </div>
  );
}
